/**
 * D16 — Notifications.
 *
 * Adds the unread state the artwork never designed (gap G-20): unread items
 * carry a dot and a tinted surface, and opening the screen marks them read.
 */

import { useEffect } from 'react';
import { FlatList, Pressable, StyleSheet, View } from 'react-native';

import { formatDateTime, type NotificationItem } from '@mfa/domain';
import { colors, radius, space, statusTone } from '../../theme';
import { EmptyState, ErrorState } from '../../components/feedback/States';
import { GlyphCircle } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { useMarkNotificationsRead, useNotifications } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

/** Icon and tone per event type, so the feed reads at a glance. */
const PRESENTATION: Record<NotificationItem['type'], { icon: string; tone: keyof typeof statusTone }> = {
  PAYMENT_CONFIRMED: { icon: 'donate', tone: 'success' },
  ORDER_ACCEPTED: { icon: 'donations', tone: 'info' },
  PREPARING: { icon: 'prepare', tone: 'progress' },
  ORDER_READY: { icon: 'delivery', tone: 'progress' },
  DISTRIBUTED: { icon: 'distribute', tone: 'progress' },
  COMPLETED: { icon: 'care', tone: 'success' },
  PROOF_AVAILABLE: { icon: 'impact', tone: 'success' },
  RECEIPT_AVAILABLE: { icon: 'donations', tone: 'neutral' },
  IMPACT_REPORT: { icon: 'info', tone: 'neutral' },
};

const FALLBACK = { icon: 'bell', tone: 'neutral' } as const;

export function NotificationsScreen({ navigation }: DonorScreenProps<'Notifications'>) {
  const t = useT();
  const query = useNotifications();
  const markRead = useMarkNotificationsRead();

  const items = query.data?.items ?? [];
  const hasUnread = items.some((item) => item.readAt === null);

  // Seeing the feed is what marks it read; done once per visit with unread items.
  useEffect(() => {
    if (hasUnread && !markRead.isPending) {
      markRead.mutate(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnread]);

  return (
    <ScreenShell
      leading="menu"
      onMenuPress={() => navigation.getParent()?.dispatch({ type: 'OPEN_DRAWER' })}
      scrollable={false}
      onRefresh={() => void query.refetch()}
      refreshing={query.isRefetching}
      testID="screen-notifications"
      title={t('Notifications')}
    >

      {query.isLoading ? (
        <View style={styles.loading}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={88} />
          ))}
        </View>
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('Nothing yet')}
          message={t('Updates about your donations will appear here.')}
        />
      ) : (
        <FlatList
          data={items}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => {
            // A type added on the server after this build still gets a row.
            const presentation = PRESENTATION[item.type] ?? FALLBACK;
            const unread = item.readAt === null;
            return (
              <Pressable
                testID={`notification-${item.id}`}
                onPress={() => markRead.mutate(item.id)}
                accessibilityRole="button"
                accessibilityLabel={`${unread ? 'Unread. ' : ''}${item.title}. ${item.body}`}
                style={[styles.item, unread && styles.itemUnread]}
              >
                <GlyphCircle
                  name={presentation.icon}
                  size={44}
                  background={statusTone[presentation.tone].bg}
                />
                <View style={styles.itemBody}>
                  <Text variant="cardTitle">{item.title}</Text>
                  <Text variant="caption" tone="secondary">
                    {item.body}
                  </Text>
                  <Text variant="caption" tone="tertiary">
                    {formatDateTime(item.createdAt)}
                  </Text>
                </View>
                {unread ? <View style={styles.unreadDot} testID="unread-dot" /> : null}
              </Pressable>
            );
          }}
        />
      )}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  title: {
    marginBottom: space.lg,
  },
  loading: {
    gap: space.md,
  },
  list: {
    gap: space.md,
    paddingBottom: space.xl,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: space.md,
    padding: space.md,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
  },
  itemUnread: {
    backgroundColor: colors.bg.brandSubtle,
  },
  itemBody: {
    flex: 1,
    gap: space.xxs,
  },
  unreadDot: {
    width: 10,
    height: 10,
    borderRadius: radius.full,
    backgroundColor: colors.bg.brand,
    marginTop: space.xs,
  },
});
